import { useState, useMemo, useCallback, useRef, useEffect, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Building2,
  FileText,
  Users,
  Banknote,
  Wand2,
  Loader2,
  ChevronDown,
  ChevronUp,
  Settings2,
  SlidersHorizontal,
  Filter,
  X,
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { useHospitalReports } from '../hooks';
import { useReportTypes } from '@/features/reports/hooks/useReportTypes';
import { localizedName } from '@/utils/localized';
import { useCurrencySymbol } from '@/hooks/useCurrencySymbol';
import useMediaQuery, { MD_BREAKPOINT } from '@/hooks/useMediaQuery';
import {
  ADMIN_COLORS,
} from '../utils/adminAnimations';
import { GlassCard } from '@/components/glass';
import { StatusBadge, STATUS_BADGE_CONFIG, STATUS_LABEL_KEY } from '../components/StatusBadge';
import { AnimatedValue } from '../components/AnimatedValue';
import { ExportWizardModal, type WizardParams } from '../components/ExportWizardModal';
import { TimeRangePicker } from '../components/TimeRangePicker';
import { PayrollPdfDocument } from '../components/PayrollPdfDocument';
import { normalizeHospitalExportData } from '../utils/export/normalizeHospitalExportData';
import { exportCsv } from '../utils/export/csvExporter';
import type { ExportLayout } from '../utils/export/exportTypes';
import type { ExportColumnId, ExportGroupBy } from '../utils/export/exportColumns';
import type { HospitalReportRow } from '../types';
import type { TimeRange } from '../utils/timeRange';
import {
  getDefaultTimeRange,
  timeRangeToApiParams,
  formatTimeRangeLabel,
  isSingleMonth,
} from '../utils/timeRange';

const STATIC_COLUMNS = [
  { id: 'reports', labelKey: 'hospitalReports.totalReports' },
  { id: 'submitted', labelKey: 'hospitalReports.submitted' },
  { id: 'approved', labelKey: 'hospitalReports.approved' },
  { id: 'rejected', labelKey: 'hospitalReports.rejected' },
  { id: 'paid', labelKey: 'hospitalReports.paid' },
  { id: 'amount', labelKey: 'hospitalReports.totalAmount' },
  { id: 'employees', labelKey: 'hospitalReports.therapistCount' },
] as const;
type StaticColumnId = (typeof STATIC_COLUMNS)[number]['id'];

const ALL_STATUSES = ['SUBMITTED', 'APPROVED', 'REJECTED', 'PAID'] as const;

export function HospitalReportsPage() {
  const { t, i18n } = useTranslation();
  const currencySymbol = useCurrencySymbol();
  const locale = i18n.language === 'he' ? 'he-IL' : 'en-US';
  const isMobile = useMediaQuery(MD_BREAKPOINT);

  const [timeRange, setTimeRange] = useState<TimeRange>(getDefaultTimeRange);
  const [isExportOpen, setIsExportOpen] = useState(false);
  const [wizardExporting, setWizardExporting] = useState(false);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [aggregated, setAggregated] = useState(true);
  const [columnsPopoverOpen, setColumnsPopoverOpen] = useState(false);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const columnsButtonRef = useRef<HTMLButtonElement>(null);
  const columnsPopoverRef = useRef<HTMLDivElement>(null);

  const [columnOrder, setColumnOrder] = useState<string[]>(
    () => STATIC_COLUMNS.map(c => c.id)
  );
  const toggleColumn = useCallback((id: string) => {
    setColumnOrder((prev) => {
      if (prev.includes(id)) {
        if (prev.length === 1) return prev;
        return prev.filter(c => c !== id);
      }
      return [...prev, id];
    });
  }, []);

  // Native drag-and-drop for table header columns
  const [draggingCol, setDraggingCol] = useState<string | null>(null);
  const [dragOverCol, setDragOverCol] = useState<string | null>(null);

  const handleHeaderDragStart = useCallback((colId: string, e: React.DragEvent) => {
    setDraggingCol(colId);
    e.dataTransfer.effectAllowed = 'move';
  }, []);

  const handleHeaderDragOver = useCallback((colId: string, e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverCol(prev => prev !== colId ? colId : prev);
  }, []);

  const handleHeaderDrop = useCallback((targetColId: string, e: React.DragEvent) => {
    e.preventDefault();
    setDraggingCol(prev => {
      if (!prev || prev === targetColId) return null;
      setColumnOrder(order => {
        const fromIdx = order.indexOf(prev);
        const toIdx = order.indexOf(targetColId);
        if (fromIdx === -1 || toIdx === -1) return order;
        const next = [...order];
        next.splice(fromIdx, 1);
        next.splice(toIdx, 0, prev);
        return next;
      });
      return null;
    });
    setDragOverCol(null);
  }, []);

  const handleHeaderDragEnd = useCallback(() => {
    setDraggingCol(null);
    setDragOverCol(null);
  }, []);

  const [selectedStatuses, setSelectedStatuses] = useState<Set<string>>(() => new Set(ALL_STATUSES));
  const allStatuses = selectedStatuses.size === ALL_STATUSES.length;

  const toggleExpand = useCallback((id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleStatus = useCallback((status: string) => {
    setSelectedStatuses((prev) => {
      const next = new Set(prev);
      if (next.has(status)) {
        if (next.size === 1) return prev;
        next.delete(status);
      } else {
        next.add(status);
      }
      return next;
    });
  }, []);

  const { data, isLoading } = useHospitalReports(timeRangeToApiParams(timeRange));

  const { data: reportTypesData } = useReportTypes({ isActive: 'true' });
  const reportTypes = reportTypesData?.reportTypes ?? [];
  const reportTypeCodes = useMemo(() => reportTypes.map((rt) => rt.code), [reportTypes]);
  const [selectedTypes, setSelectedTypes] = useState<Set<string> | null>(null);
  const effectiveSelectedTypes = selectedTypes ?? new Set(reportTypeCodes);
  const allTypes = effectiveSelectedTypes.size === reportTypeCodes.length || effectiveSelectedTypes.size === 0;

  const toggleType = useCallback((code: string) => {
    setSelectedTypes((prev) => {
      const base = prev ?? new Set(reportTypeCodes);
      const next = new Set(base);
      if (next.has(code)) {
        if (next.size === 1) return prev;
        next.delete(code);
      } else {
        next.add(code);
      }
      return next;
    });
  }, [reportTypeCodes]);

  const hospitals = data?.hospitals ?? [];
  const grandTotals = data?.grandTotals ?? { total: 0, submitted: 0, approved: 0, rejected: 0, paid: 0, totalAmount: 0, therapistCount: 0 };

  // Build all toggleable columns: static + one per report type
  const allPageColumns = useMemo(() => [
    ...STATIC_COLUMNS.map(c => ({ id: c.id, label: t(c.labelKey) })),
    ...reportTypes.map(rt => ({
      id: `rt:${rt.code}`,
      label: localizedName(rt, i18n.language),
    })),
  ], [t, reportTypes, i18n.language]);

  const allColumnsSelected = columnOrder.length === allPageColumns.length;
  const activeColumns = columnOrder.map(id => allPageColumns.find(c => c.id === id)!).filter(Boolean);

  // Recompute row data from individual reports (for status filtering)
  const computeFromReports = (reports: HospitalReportRow['reports']) => ({
    total: reports.length,
    submitted: reports.filter((r) => r.status === 'SUBMITTED').length,
    approved: reports.filter((r) => r.status === 'APPROVED').length,
    rejected: reports.filter((r) => r.status === 'REJECTED').length,
    paid: reports.filter((r) => r.status === 'PAID').length,
    totalAmount: reports
      .filter((r) => r.status !== 'REJECTED')
      .reduce((sum, r) => sum + (r.calculatedAmount ?? 0), 0),
    therapistCount: new Set(reports.map((r) => r.therapistId)).size,
  });

  const filterReports = useCallback((reports: HospitalReportRow['reports']) => {
    let filtered = reports;
    if (!allTypes) filtered = filtered.filter((r) => effectiveSelectedTypes.has(r.reportTypeCode));
    if (!allStatuses) filtered = filtered.filter((r) => selectedStatuses.has(r.status));
    return filtered;
  }, [allTypes, effectiveSelectedTypes, allStatuses, selectedStatuses]);

  const getRowData = (row: HospitalReportRow) => {
    if (allTypes && allStatuses) return row.totals;
    return computeFromReports(filterReports(row.reports));
  };

  const getRowTypeCount = (row: HospitalReportRow, typeCode: string) => {
    if (allStatuses) return row.byReportType[typeCode]?.total ?? 0;
    return filterReports(row.reports).filter(r => r.reportTypeCode === typeCode).length;
  };

  const getChildReports = (row: HospitalReportRow) => {
    const filtered = filterReports(row.reports);
    if (!aggregated) return filtered;
    const byType = new Map<string, HospitalReportRow['reports'][0] & { _aggQty: number; _aggAmount: number }>();
    for (const r of filtered) {
      const key = r.reportTypeCode;
      const existing = byType.get(key);
      if (existing) {
        existing._aggQty += r.quantity;
        existing._aggAmount += r.calculatedAmount ?? 0;
      } else {
        byType.set(key, { ...r, _aggQty: r.quantity, _aggAmount: r.calculatedAmount ?? 0 });
      }
    }
    return [...byType.values()].map((r) => ({
      ...r,
      quantity: r._aggQty,
      calculatedAmount: r._aggAmount,
      date: '',
      therapistName: '',
    }));
  };

  const computedGrandTotals = useMemo(() => {
    if (allTypes && allStatuses) return grandTotals;
    const allReports = hospitals.flatMap((h) => h.reports);
    return computeFromReports(filterReports(allReports));
  }, [hospitals, grandTotals, allTypes, allStatuses, filterReports]);

  const grandTypeTotals = useMemo(() => {
    const totals: Record<string, number> = {};
    for (const rt of reportTypes) {
      totals[rt.code] = hospitals.reduce((sum, h) => {
        if (allStatuses) return sum + (h.byReportType[rt.code]?.total ?? 0);
        return sum + filterReports(h.reports).filter(r => r.reportTypeCode === rt.code).length;
      }, 0);
    }
    return totals;
  }, [hospitals, reportTypes, allStatuses, filterReports]);

  // Active filter count (for mobile badge)
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (!allStatuses) count += 1;
    if (!allTypes) count += 1;
    if (!aggregated) count += 1;
    if (!allColumnsSelected) count += 1;
    return count;
  }, [allStatuses, allTypes, aggregated, allColumnsSelected]);

  // Close columns popover on outside click
  useEffect(() => {
    if (!columnsPopoverOpen) return;
    const handler = (e: MouseEvent) => {
      if (
        columnsPopoverRef.current && !columnsPopoverRef.current.contains(e.target as Node) &&
        columnsButtonRef.current && !columnsButtonRef.current.contains(e.target as Node)
      ) {
        setColumnsPopoverOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [columnsPopoverOpen]);

  // Export handler
  const handleExport = async (params: {
    employeeIds: Set<string>;
    statuses: Set<string>;
    reportTypes: Set<string>;
    aggregated: boolean;
    format: 'csv' | 'xlsx' | 'pdf';
    layout: ExportLayout;
    columns: ExportColumnId[];
    groupBy: ExportGroupBy;
  }) => {
    setWizardExporting(true);
    try {
      const selectedHospitals = hospitals.filter(h => params.employeeIds.has(h.hospitalId));
      const rangeLabel = formatTimeRangeLabel(timeRange, locale);
      const exportFilename = `${t('hospitalReports.title')}-${isSingleMonth(timeRange) ? timeRange.startMonth : `${timeRange.startMonth}_${timeRange.endMonth}`}`;
      const payload = normalizeHospitalExportData({
        title: t('hospitalReports.title'),
        hospitals: selectedHospitals,
        selectedTypes: params.reportTypes,
        statuses: params.statuses,
        currencySymbol,
        language: i18n.language,
        timeRange,
        columns: params.columns,
        groupBy: params.groupBy,
        aggregated: params.aggregated,
      });

      if (params.format === 'csv') {
        await exportCsv(payload, exportFilename, params.layout);
      } else if (params.format === 'xlsx') {
        const { exportXlsx } = await import('../utils/export/xlsxExporter');
        await exportXlsx(payload, exportFilename, params.layout);
      } else {
        const { exportPdf } = await import('../utils/export/pdfExporter');
        await exportPdf(payload, exportFilename, params.layout);
      }
    } finally {
      setWizardExporting(false);
    }
  };

  const exportEmployees = hospitals.map(h => ({
    id: h.hospitalId,
    name: localizedName({ name: h.hospitalName, nameEn: h.hospitalNameEn }, i18n.language),
    role: h.hospitalCode,
  }));

  const lineColor = 'color-mix(in srgb, var(--color-primary-500) 30%, transparent)';

  // ── Filter toolbar content (shared between desktop inline + mobile bottom sheet) ──
  const filterContent = (
    <>
      {/* Status pills */}
      <div className="flex flex-wrap items-center gap-1.5">
        {ALL_STATUSES.map(status => {
          const isActive = selectedStatuses.has(status);
          const cfg = STATUS_BADGE_CONFIG[status];
          return (
            <button
              key={status}
              onClick={() => toggleStatus(status)}
              className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold transition-all cursor-pointer"
              style={{
                background: isActive ? cfg.bg : 'transparent',
                color: isActive ? cfg.fg : ADMIN_COLORS.textTertiary,
                border: `1.5px solid ${isActive ? cfg.fg : ADMIN_COLORS.border}`,
                opacity: isActive ? 1 : 0.6,
              }}
            >
              {t(STATUS_LABEL_KEY[status])}
            </button>
          );
        })}
      </div>

      {/* Report type pills */}
      {reportTypes.length > 1 && (
        <div className="flex flex-wrap items-center gap-1.5">
          {reportTypes.map(rt => {
            const isActive = effectiveSelectedTypes.has(rt.code);
            return (
              <button
                key={rt.id}
                onClick={() => toggleType(rt.code)}
                className="inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-medium transition-all cursor-pointer"
                style={{
                  background: isActive ? ADMIN_COLORS.primarySoft : 'transparent',
                  color: isActive ? ADMIN_COLORS.primaryText : ADMIN_COLORS.textTertiary,
                  border: `1.5px solid ${isActive ? 'var(--color-primary-300)' : ADMIN_COLORS.border}`,
                  opacity: isActive ? 1 : 0.6,
                }}
              >
                {localizedName(rt, i18n.language)}
              </button>
            );
          })}
        </div>
      )}

      {/* Aggregated / Detailed segmented control */}
      <div
        className="inline-flex rounded-lg overflow-hidden"
        style={{ border: `1px solid ${ADMIN_COLORS.border}` }}
      >
        <button
          onClick={() => setAggregated(true)}
          className="px-3 py-1.5 text-[11px] font-semibold transition-colors"
          style={{
            backgroundColor: aggregated ? ADMIN_COLORS.primarySoft : 'transparent',
            color: aggregated ? ADMIN_COLORS.primaryText : ADMIN_COLORS.textTertiary,
          }}
        >
          {t('hospitalReports.aggregatedShort')}
        </button>
        <button
          onClick={() => setAggregated(false)}
          className="px-3 py-1.5 text-[11px] font-semibold transition-colors"
          style={{
            backgroundColor: !aggregated ? ADMIN_COLORS.primarySoft : 'transparent',
            color: !aggregated ? ADMIN_COLORS.primaryText : ADMIN_COLORS.textTertiary,
            borderInlineStart: `1px solid ${ADMIN_COLORS.border}`,
          }}
        >
          {t('hospitalReports.detailed')}
        </button>
      </div>
    </>
  );

  // ── Columns popover ──
  const columnsPopover = columnsPopoverOpen && (
    <div
      ref={columnsPopoverRef}
      className="absolute z-50 mt-1 rounded-xl shadow-lg border p-3 min-w-[200px]"
      style={{
        backgroundColor: 'rgba(255,255,255,0.95)',
        backdropFilter: 'blur(12px)',
        borderColor: ADMIN_COLORS.border,
        insetInlineEnd: 0,
        top: '100%',
      }}
    >
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: ADMIN_COLORS.textSecondary }}>
          {t('hospitalReports.configureColumns')}
        </span>
        <button
          onClick={() => setColumnOrder(allColumnsSelected ? [STATIC_COLUMNS[0].id] : allPageColumns.map(c => c.id))}
          className="text-[10px] font-semibold"
          style={{ color: 'var(--color-primary-500)' }}
        >
          {allColumnsSelected ? t('export.wizard.deselectAll') : t('export.wizard.selectAll')}
        </button>
      </div>
      <div className="space-y-1">
        {allPageColumns.map(col => {
          const isActive = columnOrder.includes(col.id);
          return (
            <label
              key={col.id}
              className="flex items-center gap-2 rounded-lg px-2 py-1.5 cursor-pointer transition-colors hover:bg-white/60"
            >
              <input
                type="checkbox"
                checked={isActive}
                onChange={() => toggleColumn(col.id)}
                className="neo-checkbox"
              />
              <span className="text-[11px] font-medium" style={{ color: ADMIN_COLORS.navy }}>
                {col.label}
              </span>
            </label>
          );
        })}
      </div>
    </div>
  );

  // ── Render cell value for a given column ──
  const renderCell = (col: { id: string; label: string }, d: ReturnType<typeof getRowData>, row: HospitalReportRow) => {
    if (col.id.startsWith('rt:')) {
      const code = col.id.slice(3);
      return <td key={col.id} className="px-3 py-2.5 font-semibold" style={{ color: ADMIN_COLORS.navy }}>{getRowTypeCount(row, code)}</td>;
    }
    const cellRenderers: Record<StaticColumnId, React.ReactNode> = {
      reports: <td key={col.id} className="px-3 py-2.5 font-semibold" style={{ color: ADMIN_COLORS.navy }}>{d.total}</td>,
      submitted: <td key={col.id} className="px-3 py-2.5"><span className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold" style={{ background: STATUS_BADGE_CONFIG.SUBMITTED.bg, color: STATUS_BADGE_CONFIG.SUBMITTED.fg }}>{d.submitted}</span></td>,
      approved: <td key={col.id} className="px-3 py-2.5"><span className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold" style={{ background: STATUS_BADGE_CONFIG.APPROVED.bg, color: STATUS_BADGE_CONFIG.APPROVED.fg }}>{d.approved}</span></td>,
      rejected: <td key={col.id} className="px-3 py-2.5"><span className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold" style={{ background: STATUS_BADGE_CONFIG.REJECTED.bg, color: STATUS_BADGE_CONFIG.REJECTED.fg }}>{d.rejected}</span></td>,
      paid: <td key={col.id} className="px-3 py-2.5"><span className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold" style={{ background: STATUS_BADGE_CONFIG.PAID.bg, color: STATUS_BADGE_CONFIG.PAID.fg }}>{d.paid}</span></td>,
      amount: <td key={col.id} className="px-3 py-2.5 font-semibold" style={{ color: ADMIN_COLORS.navy }}>{d.totalAmount.toLocaleString()} {currencySymbol}</td>,
      employees: <td key={col.id} className="px-3 py-2.5" style={{ color: ADMIN_COLORS.textSecondary }}>{d.therapistCount}</td>,
    };
    return cellRenderers[col.id as StaticColumnId];
  };

  // ── Child sub-table columns ──
  const subTableColumns = aggregated
    ? [
        { key: 'type', label: t('export.wizard.reportTypes') },
        { key: 'count', label: t('hospitalReports.totalReports') },
        { key: 'amount', label: t('hospitalReports.totalAmount') },
      ]
    : [
        { key: 'date', label: t('export.wizard.colDate') },
        { key: 'therapist', label: t('hospitalReports.therapistCount') },
        { key: 'type', label: t('export.wizard.reportTypes') },
        { key: 'status', label: t('export.wizard.colStatus') },
        { key: 'qty', label: t('reports.quantity') },
        { key: 'amount', label: t('hospitalReports.totalAmount') },
      ];

  return (
    <div className="space-y-4">
      {/* ── Header with inline stat chips ── */}
      <div className="flex items-start gap-3">
        <div
          className="flex items-center justify-center w-10 h-10 rounded-xl shrink-0"
          style={{ background: ADMIN_COLORS.primarySoft }}
        >
          <Building2 className="w-5 h-5" style={{ color: ADMIN_COLORS.primary }} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center flex-wrap gap-x-3 gap-y-1">
            <h1 className="text-lg font-bold" style={{ color: ADMIN_COLORS.navy }}>
              {t('hospitalReports.title')}
            </h1>
            {!isLoading && (
              <div className="flex items-center gap-2 text-[11px] font-medium" style={{ color: ADMIN_COLORS.textSecondary }}>
                <span className="inline-flex items-center gap-1">
                  <Building2 className="w-3 h-3" style={{ color: ADMIN_COLORS.primary }} />
                  <AnimatedValue value={hospitals.length} />
                </span>
                <span style={{ color: ADMIN_COLORS.border }}>·</span>
                <span className="inline-flex items-center gap-1">
                  <FileText className="w-3 h-3" style={{ color: '#059669' }} />
                  <AnimatedValue value={computedGrandTotals.total} />
                </span>
                <span style={{ color: ADMIN_COLORS.border }}>·</span>
                <span className="inline-flex items-center gap-1">
                  <Banknote className="w-3 h-3" style={{ color: '#d97706' }} />
                  <AnimatedValue value={computedGrandTotals.totalAmount} currency />
                </span>
                <span style={{ color: ADMIN_COLORS.border }}>·</span>
                <span className="inline-flex items-center gap-1">
                  <Users className="w-3 h-3" style={{ color: '#7c3aed' }} />
                  <AnimatedValue value={computedGrandTotals.therapistCount} />
                </span>
              </div>
            )}
          </div>
          <p className="text-xs" style={{ color: ADMIN_COLORS.textSecondary }}>
            {t('hospitalReports.subtitle')}
          </p>
        </div>
      </div>

      {/* ── Compact filter toolbar (desktop) ── */}
      {!isMobile && (
        <div className="flex items-center gap-2 flex-wrap">
          <TimeRangePicker value={timeRange} onChange={setTimeRange} compact />

          <div className="h-5 w-px mx-1" style={{ backgroundColor: ADMIN_COLORS.border }} />

          {filterContent}

          {/* Column config gear */}
          <div className="relative" style={{ marginInlineStart: 'auto' }}>
            <button
              ref={columnsButtonRef}
              onClick={() => setColumnsPopoverOpen(p => !p)}
              className="flex items-center justify-center w-8 h-8 rounded-lg transition-colors hover:bg-white/60"
              style={{
                border: `1px solid ${ADMIN_COLORS.border}`,
                color: columnsPopoverOpen ? ADMIN_COLORS.primary : ADMIN_COLORS.textSecondary,
              }}
              title={t('hospitalReports.configureColumns')}
            >
              <Settings2 className="w-4 h-4" />
            </button>
            {columnsPopover}
          </div>

          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.97 }}
            onClick={() => setIsExportOpen(true)}
            className="flex items-center gap-2 rounded-full font-semibold text-white transition-all"
            style={{
              backgroundColor: ADMIN_COLORS.primary,
              boxShadow: `0 1px 3px color-mix(in srgb, ${ADMIN_COLORS.primary} 30%, transparent)`,
              fontSize: '0.8rem',
              padding: '0.5rem 1rem',
            }}
          >
            <Wand2 size={16} strokeWidth={3} />
            <span>{t('hospitalReports.generateWizard')}</span>
          </motion.button>
        </div>
      )}

      {/* ── Mobile toolbar ── */}
      {isMobile && (
        <div className="flex items-center gap-2">
          <TimeRangePicker value={timeRange} onChange={setTimeRange} compact />
          <div className="flex-1" />
          <button
            onClick={() => setMobileFiltersOpen(true)}
            className="relative flex items-center justify-center w-9 h-9 rounded-lg"
            style={{ border: `1px solid ${ADMIN_COLORS.border}`, color: ADMIN_COLORS.textSecondary }}
          >
            <Filter className="w-4 h-4" />
            {activeFilterCount > 0 && (
              <span
                className="absolute -top-1.5 -end-1.5 w-4 h-4 rounded-full text-[9px] font-bold text-white flex items-center justify-center"
                style={{ backgroundColor: ADMIN_COLORS.primary }}
              >
                {activeFilterCount}
              </span>
            )}
          </button>
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.97 }}
            onClick={() => setIsExportOpen(true)}
            className="flex items-center gap-1.5 rounded-full font-semibold text-white"
            style={{
              backgroundColor: ADMIN_COLORS.primary,
              fontSize: '0.75rem',
              padding: '0.45rem 0.85rem',
            }}
          >
            <Wand2 size={14} strokeWidth={3} />
          </motion.button>
        </div>
      )}

      {/* ── Mobile filter bottom sheet ── */}
      <AnimatePresence>
        {isMobile && mobileFiltersOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/30 z-40"
              onClick={() => setMobileFiltersOpen(false)}
            />
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="fixed inset-x-0 bottom-0 z-50 rounded-t-2xl p-4 pb-8 space-y-4"
              style={{
                backgroundColor: 'rgba(255,255,255,0.97)',
                backdropFilter: 'blur(16px)',
                maxHeight: '70vh',
                overflowY: 'auto',
              }}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <SlidersHorizontal className="w-4 h-4" style={{ color: ADMIN_COLORS.primary }} />
                  <span className="text-sm font-bold" style={{ color: ADMIN_COLORS.navy }}>
                    {t('hospitalReports.filterCount', { count: activeFilterCount })}
                  </span>
                </div>
                <button onClick={() => setMobileFiltersOpen(false)}>
                  <X className="w-5 h-5" style={{ color: ADMIN_COLORS.textSecondary }} />
                </button>
              </div>

              {filterContent}

              {/* Column config */}
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-wider mb-2" style={{ color: ADMIN_COLORS.textSecondary }}>
                  {t('hospitalReports.configureColumns')}
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {allPageColumns.map(col => {
                    const isActive = columnOrder.includes(col.id);
                    return (
                      <button
                        key={col.id}
                        onClick={() => toggleColumn(col.id)}
                        className="inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-medium transition-all"
                        style={{
                          background: isActive ? ADMIN_COLORS.primarySoft : 'transparent',
                          color: isActive ? ADMIN_COLORS.primaryText : ADMIN_COLORS.textTertiary,
                          border: `1.5px solid ${isActive ? 'var(--color-primary-300)' : ADMIN_COLORS.border}`,
                          opacity: isActive ? 1 : 0.6,
                        }}
                      >
                        {col.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* ── Data ── */}
      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-6 h-6 animate-spin" style={{ color: ADMIN_COLORS.primary }} />
        </div>
      ) : hospitals.length === 0 ? (
        <GlassCard className="p-8 text-center">
          <Building2 className="w-8 h-8 mx-auto mb-2" style={{ color: ADMIN_COLORS.textTertiary }} />
          <p className="text-sm" style={{ color: ADMIN_COLORS.textSecondary }}>
            {t('hospitalReports.noData')}
          </p>
        </GlassCard>
      ) : isMobile ? (
        /* ── Mobile card layout ── */
        <div className="space-y-3">
          {hospitals.map((row) => {
            const d = getRowData(row);
            const isExpanded = expandedIds.has(row.hospitalId);
            const childReports = isExpanded ? getChildReports(row) : [];
            return (
              <GlassCard key={row.hospitalId} className="overflow-hidden">
                <button
                  className="w-full p-3 flex items-center gap-3 text-start"
                  onClick={() => toggleExpand(row.hospitalId)}
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold truncate" style={{ color: ADMIN_COLORS.navy }}>
                      {localizedName({ name: row.hospitalName, nameEn: row.hospitalNameEn }, i18n.language)}
                    </div>
                    <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                      {d.submitted > 0 && (
                        <span className="inline-flex items-center rounded-full px-1.5 py-0.5 text-[9px] font-semibold" style={{ background: STATUS_BADGE_CONFIG.SUBMITTED.bg, color: STATUS_BADGE_CONFIG.SUBMITTED.fg }}>
                          {d.submitted}
                        </span>
                      )}
                      {d.approved > 0 && (
                        <span className="inline-flex items-center rounded-full px-1.5 py-0.5 text-[9px] font-semibold" style={{ background: STATUS_BADGE_CONFIG.APPROVED.bg, color: STATUS_BADGE_CONFIG.APPROVED.fg }}>
                          {d.approved}
                        </span>
                      )}
                      {d.rejected > 0 && (
                        <span className="inline-flex items-center rounded-full px-1.5 py-0.5 text-[9px] font-semibold" style={{ background: STATUS_BADGE_CONFIG.REJECTED.bg, color: STATUS_BADGE_CONFIG.REJECTED.fg }}>
                          {d.rejected}
                        </span>
                      )}
                      {d.paid > 0 && (
                        <span className="inline-flex items-center rounded-full px-1.5 py-0.5 text-[9px] font-semibold" style={{ background: STATUS_BADGE_CONFIG.PAID.bg, color: STATUS_BADGE_CONFIG.PAID.fg }}>
                          {d.paid}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="text-end shrink-0">
                    <div className="text-sm font-bold" style={{ color: ADMIN_COLORS.navy }}>
                      {d.totalAmount.toLocaleString()} {currencySymbol}
                    </div>
                    <div className="text-[10px]" style={{ color: ADMIN_COLORS.textSecondary }}>
                      {d.total} {t('hospitalReports.totalReports').toLowerCase()}
                    </div>
                  </div>
                  {isExpanded ? (
                    <ChevronUp className="w-4 h-4 shrink-0 text-slate-400" />
                  ) : (
                    <ChevronDown className="w-4 h-4 shrink-0 text-slate-400" />
                  )}
                </button>

                <AnimatePresence>
                  {isExpanded && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="overflow-hidden"
                    >
                      <div className="px-3 pb-3 space-y-1.5" style={{ borderTop: `1px solid ${ADMIN_COLORS.border}` }}>
                        {childReports.map((r, _idx) => {
                          const typeName = r.reportTypeObj ? localizedName(r.reportTypeObj, i18n.language) : r.reportTypeCode;
                          return (
                            <div
                              key={aggregated ? `agg-${r.reportTypeCode}` : r.id}
                              className="flex items-center gap-2 rounded-lg px-2 py-1.5 mt-1.5"
                              style={{ backgroundColor: 'rgba(248,250,252,0.5)' }}
                            >
                              <div className="flex-1 min-w-0">
                                <div className="text-[11px] font-medium truncate" style={{ color: ADMIN_COLORS.navy }}>
                                  {aggregated ? typeName : `${r.date} · ${r.therapistName}`}
                                </div>
                                {!aggregated && (
                                  <div className="text-[10px] truncate" style={{ color: ADMIN_COLORS.textTertiary }}>
                                    {typeName}
                                  </div>
                                )}
                              </div>
                              {!aggregated && <StatusBadge status={r.status} />}
                              <div className="text-[10px] text-end shrink-0" style={{ color: ADMIN_COLORS.textSecondary }}>
                                <span>×{r.quantity}</span>
                              </div>
                              <div
                                className={`text-[11px] font-semibold text-end shrink-0${r.status === 'REJECTED' ? ' line-through opacity-50' : ''}`}
                                style={{ color: ADMIN_COLORS.navy }}
                              >
                                {r.calculatedAmount != null ? `${r.calculatedAmount.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })} ${currencySymbol}` : '-'}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </GlassCard>
            );
          })}

          {/* Mobile grand total card */}
          <GlassCard className="p-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-bold" style={{ color: ADMIN_COLORS.navy }}>
                {t('hospitalReports.grandTotal')}
              </span>
              <span className="text-sm font-bold" style={{ color: ADMIN_COLORS.navy }}>
                {computedGrandTotals.totalAmount.toLocaleString()} {currencySymbol}
              </span>
            </div>
            <div className="flex items-center gap-3 mt-1 text-[10px]" style={{ color: ADMIN_COLORS.textSecondary }}>
              <span>{computedGrandTotals.total} {t('hospitalReports.totalReports').toLowerCase()}</span>
              <span>·</span>
              <span>{computedGrandTotals.therapistCount} {t('hospitalReports.therapistCount').toLowerCase()}</span>
            </div>
          </GlassCard>
        </div>
      ) : (
        /* ── Desktop table layout ── */
        <GlassCard className="overflow-x-auto relative">
          <table className="w-full text-xs" style={{ borderCollapse: 'separate', borderSpacing: 0 }}>
            <colgroup>
              <col />{/* Hospital name */}
              {activeColumns.map(col => (
                <col
                  key={col.id}
                  style={{
                    background: draggingCol === col.id
                      ? 'rgba(59,130,246,0.06)'
                      : dragOverCol === col.id
                        ? 'rgba(59,130,246,0.12)'
                        : undefined,
                  }}
                />
              ))}
              <col />{/* Chevron */}
            </colgroup>
            <thead>
              <tr style={{ borderBottom: `1px solid ${ADMIN_COLORS.border}` }}>
                <th className="px-3 py-2.5 text-start font-semibold whitespace-nowrap" style={{ color: ADMIN_COLORS.textSecondary }}>
                  {t('hospitalReports.hospitalName')}
                </th>
                {activeColumns.map(col => (
                  <th
                    key={col.id}
                    draggable
                    onDragStart={(e) => handleHeaderDragStart(col.id, e)}
                    onDragOver={(e) => handleHeaderDragOver(col.id, e)}
                    onDrop={(e) => handleHeaderDrop(col.id, e)}
                    onDragEnd={handleHeaderDragEnd}
                    className="px-3 py-2.5 text-start font-semibold whitespace-nowrap cursor-grab active:cursor-grabbing"
                    style={{
                      color: ADMIN_COLORS.textSecondary,
                      borderInlineStart: dragOverCol === col.id ? '2px solid var(--color-primary-400)' : '2px solid transparent',
                    }}
                  >
                    {col.label}
                  </th>
                ))}
                <th className="px-3 py-2.5" />
              </tr>
            </thead>
            <tbody>
              {hospitals.map((row) => {
                const d = getRowData(row);
                const isExpanded = expandedIds.has(row.hospitalId);
                const childReports = isExpanded ? getChildReports(row) : [];
                return (
                  <Fragment key={row.hospitalId}>
                    <tr
                      className="transition-colors hover:bg-white/40 cursor-pointer"
                      style={{ borderBottom: `1px solid ${ADMIN_COLORS.border}` }}
                      onClick={() => toggleExpand(row.hospitalId)}
                    >
                      <td className="px-3 py-2.5 font-medium whitespace-nowrap" style={{ color: ADMIN_COLORS.navy }}>
                        {localizedName({ name: row.hospitalName, nameEn: row.hospitalNameEn }, i18n.language)}
                      </td>
                      {activeColumns.map(col => renderCell(col, d, row))}
                      <td className="ps-1 pe-3 py-2.5">
                        {isExpanded ? (
                          <ChevronUp className="h-4 w-4 text-slate-500" />
                        ) : (
                          <ChevronDown className="h-4 w-4 text-slate-500" />
                        )}
                      </td>
                    </tr>
                    {/* ── Nested sub-table for expanded rows ── */}
                    <AnimatePresence>
                      {isExpanded && (
                        <tr>
                          <td colSpan={activeColumns.length + 2} className="p-0">
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: 'auto', opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.2 }}
                              className="overflow-hidden"
                            >
                              <div className="px-3 py-2" style={{ backgroundColor: 'rgba(248,250,252,0.4)' }}>
                                <table className="w-full text-xs">
                                  <thead>
                                    <tr>
                                      {/* Tree connector column */}
                                      <th className="w-6" />
                                      {subTableColumns.map(sc => (
                                        <th
                                          key={sc.key}
                                          className="px-2 py-1.5 text-start font-medium text-[10px] uppercase tracking-wider"
                                          style={{ color: ADMIN_COLORS.textTertiary }}
                                        >
                                          {sc.label}
                                        </th>
                                      ))}
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {childReports.map((r, idx) => {
                                      const isLast = idx === childReports.length - 1;
                                      const typeName = r.reportTypeObj ? localizedName(r.reportTypeObj, i18n.language) : r.reportTypeCode;
                                      const empRole = !aggregated && r.therapistEmploymentRole
                                        ? localizedName(r.therapistEmploymentRole, i18n.language)
                                        : '';
                                      return (
                                        <tr
                                          key={aggregated ? `agg-${r.reportTypeCode}` : r.id}
                                          className="hover:bg-white/30"
                                          style={{ borderBottom: `1px solid ${ADMIN_COLORS.border}` }}
                                        >
                                          {/* Tree connector */}
                                          <td className="relative w-6 py-1.5">
                                            <div style={{
                                              position: 'absolute',
                                              insetInlineStart: '0.5rem',
                                              top: 0,
                                              bottom: isLast ? '50%' : 0,
                                              width: '1px',
                                              backgroundColor: lineColor,
                                            }} />
                                            <div style={{
                                              position: 'absolute',
                                              insetInlineStart: '0.5rem',
                                              top: '50%',
                                              width: '0.4rem',
                                              height: '1px',
                                              backgroundColor: lineColor,
                                            }} />
                                            <div style={{
                                              position: 'absolute',
                                              insetInlineStart: 'calc(0.5rem + 0.4rem - 2px)',
                                              top: 'calc(50% - 2px)',
                                              width: '4px',
                                              height: '4px',
                                              borderRadius: '50%',
                                              backgroundColor: lineColor,
                                            }} />
                                          </td>
                                          {aggregated ? (
                                            <>
                                              <td className="px-2 py-1.5 text-[11px] font-medium" style={{ color: ADMIN_COLORS.navy }}>
                                                {typeName}
                                              </td>
                                              <td className="px-2 py-1.5 text-[11px]" style={{ color: ADMIN_COLORS.textSecondary }}>
                                                ×{r.quantity}
                                              </td>
                                              <td className={`px-2 py-1.5 text-[11px] font-semibold${r.status === 'REJECTED' ? ' line-through opacity-50' : ''}`} style={{ color: ADMIN_COLORS.navy }}>
                                                {r.calculatedAmount != null ? `${r.calculatedAmount.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })} ${currencySymbol}` : '-'}
                                              </td>
                                            </>
                                          ) : (
                                            <>
                                              <td className="px-2 py-1.5 text-[11px]" style={{ color: ADMIN_COLORS.textSecondary }}>
                                                {r.date}
                                              </td>
                                              <td className="px-2 py-1.5 text-[11px]" style={{ color: ADMIN_COLORS.navy }}>
                                                {r.therapistName}
                                                {empRole && (
                                                  <span className="text-[10px] ms-1" style={{ color: ADMIN_COLORS.textTertiary }}>
                                                    {empRole}
                                                  </span>
                                                )}
                                              </td>
                                              <td className="px-2 py-1.5 text-[11px]" style={{ color: ADMIN_COLORS.textSecondary }}>
                                                {typeName}
                                              </td>
                                              <td className="px-2 py-1.5">
                                                <StatusBadge status={r.status} />
                                              </td>
                                              <td className="px-2 py-1.5 text-[11px]" style={{ color: ADMIN_COLORS.textSecondary }}>
                                                ×{r.quantity}
                                              </td>
                                              <td className={`px-2 py-1.5 text-[11px] font-semibold${r.status === 'REJECTED' ? ' line-through opacity-50' : ''}`} style={{ color: ADMIN_COLORS.navy }}>
                                                {r.calculatedAmount != null ? `${r.calculatedAmount.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })} ${currencySymbol}` : '-'}
                                              </td>
                                            </>
                                          )}
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>
                            </motion.div>
                          </td>
                        </tr>
                      )}
                    </AnimatePresence>
                  </Fragment>
                );
              })}
            </tbody>
            {/* ── Sticky grand total footer ── */}
            <tfoot>
              <tr
                style={{
                  position: 'sticky',
                  bottom: 0,
                  backgroundColor: 'rgba(241, 245, 249, 0.95)',
                  backdropFilter: 'blur(8px)',
                  borderTop: `2px solid ${ADMIN_COLORS.border}`,
                  zIndex: 10,
                }}
              >
                <td className="px-3 py-2.5 font-bold" style={{ color: ADMIN_COLORS.navy }}>
                  {t('hospitalReports.grandTotal')}
                </td>
                {activeColumns.map(col => {
                  if (col.id.startsWith('rt:')) {
                    const code = col.id.slice(3);
                    return <td key={col.id} className="px-3 py-2.5 font-bold" style={{ color: ADMIN_COLORS.navy }}>{grandTypeTotals[code] ?? 0}</td>;
                  }
                  const totalRenderers: Record<StaticColumnId, React.ReactNode> = {
                    reports: <td key={col.id} className="px-3 py-2.5 font-bold" style={{ color: ADMIN_COLORS.navy }}>{computedGrandTotals.total}</td>,
                    submitted: <td key={col.id} className="px-3 py-2.5 font-bold" style={{ color: STATUS_BADGE_CONFIG.SUBMITTED.fg }}>{computedGrandTotals.submitted}</td>,
                    approved: <td key={col.id} className="px-3 py-2.5 font-bold" style={{ color: STATUS_BADGE_CONFIG.APPROVED.fg }}>{computedGrandTotals.approved}</td>,
                    rejected: <td key={col.id} className="px-3 py-2.5 font-bold" style={{ color: STATUS_BADGE_CONFIG.REJECTED.fg }}>{computedGrandTotals.rejected}</td>,
                    paid: <td key={col.id} className="px-3 py-2.5 font-bold" style={{ color: STATUS_BADGE_CONFIG.PAID.fg }}>{computedGrandTotals.paid}</td>,
                    amount: <td key={col.id} className="px-3 py-2.5 font-bold" style={{ color: ADMIN_COLORS.navy }}>{computedGrandTotals.totalAmount.toLocaleString()} {currencySymbol}</td>,
                    employees: <td key={col.id} className="px-3 py-2.5 font-bold" style={{ color: ADMIN_COLORS.textSecondary }}>{computedGrandTotals.therapistCount}</td>,
                  };
                  return totalRenderers[col.id as StaticColumnId];
                })}
                <td className="px-3 py-2.5" />
              </tr>
            </tfoot>
          </table>
        </GlassCard>
      )}

      {/* Export wizard */}
      <ExportWizardModal
        isOpen={isExportOpen}
        onClose={() => setIsExportOpen(false)}
        employees={exportEmployees}
        statuses={['PAID', 'APPROVED', 'SUBMITTED', 'REJECTED']}
        reportTypes={reportTypes.map(rt => ({ code: rt.code, name: localizedName(rt, i18n.language) }))}
        onExport={handleExport}
        isPending={wizardExporting}
        titleKey="export.wizard.hospitalReportsTitle"
        step1LabelKey="export.wizard.selectHospitals"
        step1SearchKey="export.wizard.searchHospitals"
        layoutPerItemKey="export.wizard.layoutPerHospital"
        buildPreviewDocument={(params: WizardParams) => {
          const payload = normalizeHospitalExportData({
            title: t('hospitalReports.title'),
            hospitals: hospitals.filter(h => params.employeeIds.has(h.hospitalId)),
            selectedTypes: params.reportTypes,
            statuses: params.statuses,
            currencySymbol,
            language: i18n.language,
            timeRange,
            columns: params.columns,
            groupBy: params.groupBy,
            aggregated: params.aggregated,
          });
          return <PayrollPdfDocument payload={payload} perEmployeePage={params.layout === 'per-employee'} />;
        }}
      />
    </div>
  );
}
